import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { NativeIntegrationService } from './native-integration.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Md5 } from 'ts-md5';
import { BehaviorSubject, combineLatest, merge, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TermsService implements OnDestroy {
  currentUserDidAcceptTerms$ = new BehaviorSubject<boolean>(false);
  justAcceptedTerms$ = new BehaviorSubject<boolean>(false);
  subscriptions: Subscription[] = [];
  constructor(
    private authService: AuthService,
    private nativeIntegrationService: NativeIntegrationService,
    private dataService: DataService
  ) {
    const sub = combineLatest([
      this.authService.username$,
      this.dataService.legals$,
      this.justAcceptedTerms$,
    ]).subscribe(async ([username, legals]) => {
      console.log(
        '[TERMS]: Subscribe ' + username + ' ' + JSON.stringify(legals)
      );
      const version = legals?.version;
      if (!version) {
        return;
      }
      const didAcceptTerms = await this.didAcceptTerms(version);
      this.currentUserDidAcceptTerms$.next(didAcceptTerms);
    });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async acceptTerms(version: string) {
    try {
      if (!version) {
        return;
      }
      const md5 = new Md5();
      const username =
        this.authService.username$.getValue() ??
        (await this.authService.loadUsername()) ??
        localStorage.getItem('username');
      console.log(`acceptTerms ${username} ${version}`);
      const hash = md5.appendStr(username!).end(false) as string;
      await this.nativeIntegrationService.spinTillReady();
      if (this.nativeIntegrationService.isNative$.getValue()) {
        const x = await this.nativeIntegrationService.storage.secureStoreLoad$(
          'termsAccepted',
          {
            useBiometrics: false,
            usePinCode: 'false',
          }
        );
        console.log(`acceptTerms ${JSON.stringify(x, null, 2)}`);
        if (x.data && x.data.raw) {
          const parsed = JSON.parse(x.data.raw);
          if (typeof parsed === 'object') {
            parsed[hash] = version;
            await this.nativeIntegrationService.storage.secureStoreSave$(
              JSON.stringify(parsed),
              'terms',
              false,
              'false'
            );
          } else {
            await this.nativeIntegrationService.storage.secureStoreSave$(
              JSON.stringify({ [hash]: version }),
              'terms',
              false,
              'false'
            );
          }
        } else {
          await this.nativeIntegrationService.storage.secureStoreSave$(
            JSON.stringify({ [hash]: version }),
            'terms',
            false,
            'false'
          );
        }
        this.justAcceptedTerms$.next(true);
      } else {
        const raw = localStorage.getItem('termsAccepted');
        const val = raw?.startsWith('{') ? raw : '{}';
        const terms = JSON.parse(val);
        if (terms && typeof terms === 'object') {
          terms[hash] = version;
          localStorage.setItem('termsAccepted', JSON.stringify(terms));
        } else {
          localStorage.setItem(
            'termsAccepted',
            JSON.stringify({ [hash]: version })
          );
        }
        this.justAcceptedTerms$.next(true);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async didAcceptTerms(version: string) {
    try {
      if (!version) {
        return false;
      }
      const md5 = new Md5();
      const username = this.authService.username$.getValue();
      if (!username) {
        return false;
      }
      console.log(`didAcceptTerms ${username} ${version}`);
      const hash = md5.appendStr(username).end(false) as string;
      await this.nativeIntegrationService.spinTillReady();
      if (this.nativeIntegrationService.isNative$.getValue()) {
        const x = await this.nativeIntegrationService.storage.secureStoreLoad$(
          'terms',
          { useBiometrics: false, usePinCode: 'false' }
        );
        console.log(`x ${JSON.stringify(x, null, 2)}`);
        if (x.data && x.data.raw) {
          const parsed = JSON.parse(x.data.raw);
          console.log(JSON.stringify(parsed, null, 2));
          if (typeof parsed === 'object') {
            return parsed[hash] === version;
          } else {
            const { termsVersion, termsAccepted } = parsed;
            if (termsVersion && termsVersion >= version && termsAccepted) {
              await this.acceptTerms(version);
              return true;
            } else {
              return false;
            }
          }
        }
        return false;
      } else {
        const raw = localStorage.getItem('termsAccepted');
        const val = raw?.startsWith('{') ? raw : '{}';
        const terms = JSON.parse(val);
        if (typeof terms === 'object') {
          return terms[hash] === version;
        } else {
          const oldVersion = localStorage.getItem('termsVersion');
          if (
            oldVersion &&
            oldVersion >= this.dataService.legals$.value.version &&
            localStorage.getItem('termsAccepted')
          ) {
            this.acceptTerms(version);
            return true;
          } else {
            return false;
          }
        }
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}
